/**
 * sitesToken — 현장관리(iggg-sites) Firestore REST 용 Bearer 토큰.
 *
 * 익명 로그인을 실직원 신원으로 대체(보안 웨이브 3, 2026-07-28). iggg-sites 는
 * 이 앱의 Firebase 프로젝트(iggg-schedule)와 다른 프로젝트라 직원 idToken 을 그대로
 * 못 쓴다 — 백엔드가 승인 직원인지 대조하고 iggg-sites 커스텀 토큰을 발급한다:
 *   ① 직원 idToken: 앱 세션(getIdToken) → 없으면 iggg_sso 쿠키의 refreshToken 교환
 *   ② POST /api/staff/firestore-token {scope:"sites"} → 커스텀 토큰
 *   ③ signInWithCustomToken(iggg-sites) → idToken
 * 어느 단계든 실패하면 익명 signUp 으로 폴백(전환기 — 규칙 잠금 전에는 무중단,
 * 잠근 뒤에는 폴백도 자연 실패해 조회가 빈다).
 *
 * 캐시: 세션 1회(hubExec·hubWrite·extraWorks 공용). 교환 idToken 은 1시간 유효.
 */
import { getIdToken } from "../auth/session";
import { API_BASE_URL } from "../utils";

const SITES_KEY = import.meta.env.VITE_SITES_API_KEY || "AIzaSyA8U-YmNURLO3M7S6ZlAti_IrDHwvQdrJ8";
// 실측보고서(site-report-63959) 웹 키 — ig-site-report index.html firebaseConfig 와 동일(공개 식별자)
const SURVEY_KEY = import.meta.env.VITE_SURVEY_API_KEY || "AIzaSyAv2Ls20EtMC6km1-DCG1tzWeI8DYRvNvY";
const SCHED_KEY = import.meta.env.VITE_SCHEDULE_API_KEY || "AIzaSyAks6Jg7KiIOv9rWmAlnXcC8vEnNZvDbDo";

// iggg_sso 쿠키(.igggstudio.com 공용, {email, refreshToken}) — ig works 로그인이 심는다.
function ssoRefreshToken() {
  try {
    if (typeof document === "undefined") return "";
    const m = document.cookie.match(/(?:^|; )iggg_sso=([^;]*)/);
    if (!m) return "";
    return JSON.parse(decodeURIComponent(m[1]) || "null")?.refreshToken || "";
  } catch {
    return "";
  }
}

/** 직원 idToken(iggg-schedule) — 앱 세션 우선, 없으면 SSO 쿠키 교환. 없으면 "". */
async function staffIdToken() {
  try {
    const t = await getIdToken();
    if (t) return t;
  } catch { /* 쿠키 교환으로 */ }
  const rt = ssoRefreshToken();
  if (!rt) return "";
  const r = await fetch(`https://securetoken.googleapis.com/v1/token?key=${SCHED_KEY}`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(rt)}`,
  });
  if (!r.ok) throw new Error(`securetoken ${r.status}`);
  return (await r.json()).id_token || "";
}

/** 실직원 신원의 위성 프로젝트 idToken(scope: sites|survey). 실패는 던지지 않고 null. */
async function staffSatelliteToken(scope, apiKey) {
  try {
    const idt = await staffIdToken();
    if (!idt) throw new Error("직원 세션 없음");
    const r = await fetch(`${API_BASE_URL}/api/staff/firestore-token`, {
      method: "POST",
      headers: { Authorization: `Bearer ${idt}`, "Content-Type": "application/json" },
      body: JSON.stringify({ scope }),
    });
    if (!r.ok) throw new Error(`firestore-token ${r.status}`);
    const custom = (await r.json())?.token;
    if (!custom) throw new Error("커스텀 토큰 없음");
    const r2 = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:signInWithCustomToken?key=${apiKey}`,
      {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: custom, returnSecureToken: true }),
      },
    );
    if (!r2.ok) throw new Error(`signInWithCustomToken ${r2.status}`);
    return (await r2.json()).idToken || null;
  } catch (e) {
    console.warn(`[${scope}] 직원 인증 실패:`, e?.message || e);
    return null;
  }
}

let _tokenP = null;

/** iggg-sites Firestore REST 용 idToken(실패 시 null). 세션 단위 캐시. */
export function sitesToken() {
  if (_tokenP) return _tokenP;
  _tokenP = staffSatelliteToken("sites", SITES_KEY)
    .then((t) => t || fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${SITES_KEY}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ returnSecureToken: true }),
    }).then((r) => r.json()).then((j) => j.idToken))
    .catch(() => { _tokenP = null; return null; });
  return _tokenP;
}

let _surveyP = null;

/** 실측보고서(site-report-63959) Firestore REST 용 idToken(실패 시 null). 세션 단위 캐시.
 *  목록 조회 규칙이 staff 클레임을 요구하므로 익명 폴백은 두지 않는다(보안 웨이브 4). */
export function surveyToken() {
  if (_surveyP) return _surveyP;
  _surveyP = staffSatelliteToken("survey", SURVEY_KEY)
    .then((t) => { if (!t) _surveyP = null; return t; });
  return _surveyP;
}
